import { Component, DestroyRef, ElementRef, EventEmitter, Output, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { NavigationStart } from '@angular/router';
import { Client } from '@df/core/service/client';
import { AdvancedCommerceSearchFormComponentDef } from '@df/module-advanced-commerce';
import { SearchResult } from '@df/search/search-result';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { DfUiModule } from '@df/ui/df-ui.module';
import { TranslationModule } from '@df/ui/translation/translation.module';

@Component({
    selector: 'advanced-commerce-search-form',
    templateUrl: './../../../../search/ui/search.html',
    styleUrl: './../../../../search/ui/search.scss',
    standalone: true,
    imports: [DfUiModule, FormsModule, TranslationModule, SizeClassDirective]
})
export class AdvancedCommerceSearchFormComponent extends AdvancedCommerceSearchFormComponentDef {
    readonly client = inject(Client);
    private elementRef = inject(ElementRef);
    protected destroyRef = inject(DestroyRef);

    @Output()
    closed = new EventEmitter<boolean>();
    /**
     * Icon when form is visible
     */
    @ViewChild('inputBoxOn', { read: ElementRef })
    private inputBoxOnRef: ElementRef<HTMLElement> | undefined;
    /**
     * Icon when form is not visible
     */
    @ViewChild('inputBoxOff', { read: ElementRef })
    private inputBoxOffRef: ElementRef<HTMLElement> | undefined;
    /**
     * Currently set animation shift
     */
    private animationShift = 0;

    override ngOnInit() {
        super.ngOnInit();
        //  this.subscriptions.add(this.status.subscribe(() => this.detectChanges()));
        this.subscriptions.add(
            this.app.events.on('close-search-tray').subscribe(() => {
                if (this.isFocus) {
                    this.close();
                }
            })
        );
        this.subscriptions.add(
            this.meta.routerEvents.subscribe(event => {
                if (event instanceof NavigationStart) {
                    this.close();
                }
            })
        );
        this.status.subject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.detectChanges());
    }
    keyup(event: KeyboardEvent): void {
        if (event.key === 'Enter' || event.code === 'Enter') {
            this.input?.nativeElement.blur();
            this.goToSearch();
            this.close();
        }
    }

    close(): void {
        this.toggleVisibility(false);
        this.closed.emit(true);
        this.detectChanges();
        this.reset();
    }
    override reset(): void {
        super.reset();
        this.detectChanges();
    }
    /**
     * To show opening/closing animation we need to know distance between the form (visible on focus) and the icon (visible on blur).
     */
    private updateAnimationShift() {
        if (this.app.isPlatformBrowser) {
            if (!!this.inputBoxOffRef || !!this.inputBoxOnRef) {
                const animationShift =
                    (this.isFocus ? this.animationShift : 0) +
                    (this.inputBoxOffRef?.nativeElement.getBoundingClientRect().right || 0) -
                    (this.inputBoxOnRef?.nativeElement.getBoundingClientRect().right || 0);
                this.animationShift = animationShift;
                this.elementRef.nativeElement.style.setProperty('--animation-shift', `${animationShift}px`);
            }
        }
    }

    toggleVisibility(to: boolean) {
        this.isFocus = to;

        if (this.app.client.isX || this.app.client.isL) {
            this.updateAnimationShift();
        }

        this.timeoutService.setTimeout(() => {
            if (to) {
                this.renderer2.addClass(this.elementRef.nativeElement, '_is-focused');
            } else {
                this.renderer2.removeClass(this.elementRef.nativeElement, '_is-focused');
            }
        }, 500);
    }

    get termModel() {
        return this.term;
    }

    set termModel(value: string) {
        this.term = value;
    }

    override goToSearch(): void {
        // cancel term change search request before route resolver search request
        (<any>this.onTermChange).cancel();
        super.goToSearch();
    }

    override _onTermChange(): void {
        if (this.isValid()) {
            super._onTermChange();
        }
    }

    protected override isValid() {
        return !!(this.term && this.term.length >= this.validTermLength);
    }

    protected override onSearchResult(result: SearchResult | undefined): void {
        this.status.reset();
        super.onSearchResult(result);
    }
}
